import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { X, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const RentalUpdateModal = ({ isOpen, onClose, rental, onSuccess, language = 'pt-BR' }) => {
  const [loading, setLoading] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [formData, setFormData] = useState({
    tipo: 'info_geral',
    titulo: '',
    descricao: '',
    localizacao: '',
    custo_adicional: 0,
    prioridade: 'media'
  });
  const [fotos, setFotos] = useState([]);

  // Upload REAL de fotos para GridFS
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhotos(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const uploadedUrls = [];
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API_URL}/api/trucks/upload-image`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        uploadedUrls.push(response.data.url);
      }
      
      setFotos([...fotos, ...uploadedUrls]);
      toast.success(`${files.length} ${language === 'pt-BR' ? 'fotos enviadas!' : 'photos uploaded!'}`);
    } catch (error) {
      console.error('Erro upload:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao enviar fotos' : 'Error uploading photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.titulo || !formData.descricao) {
      toast.error(language === 'pt-BR' ? 'Preencha título e descrição' : 'Fill in title and description');
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      const userRes = await axios.get(`${API_URL}/api/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const updateData = {
        ...formData,
        fotos: fotos,
        reportado_por: userRes.data.username,
        reportado_por_nome: userRes.data.full_name || userRes.data.username
      };

      await axios.post(`${API_URL}/api/rentals/${rental.id}/updates`, updateData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      toast.success(language === 'pt-BR' ? 'Atualização adicionada!' : 'Update added!');
      onSuccess();
      onClose();
      
      setFormData({
        tipo: 'info_geral',
        titulo: '',
        descricao: '',
        localizacao: '',
        custo_adicional: 0,
        prioridade: 'media'
      });
      setFotos([]);
    } catch (error) {
      console.error('Erro:', error);
      toast.error(language === 'pt-BR' ? 'Erro ao adicionar atualização' : 'Error adding update');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            📝 {language === 'pt-BR' ? 'Reportar Atualização' : 'Report Update'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>{language === 'pt-BR' ? 'Tipo de Atualização' : 'Update Type'}</Label>
            <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info_geral">📢 {language === 'pt-BR' ? 'Informação Geral' : 'General Information'}</SelectItem>
                <SelectItem value="problema">⚠️ {language === 'pt-BR' ? 'Problema/Avaria' : 'Problem/Issue'}</SelectItem>
                <SelectItem value="manutencao_realizada">🔧 {language === 'pt-BR' ? 'Manutenção Realizada' : 'Maintenance Done'}</SelectItem>
                <SelectItem value="outro">📝 {language === 'pt-BR' ? 'Outro' : 'Other'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.tipo === 'problema' && (
            <div>
              <Label>{language === 'pt-BR' ? 'Prioridade' : 'Priority'}</Label>
              <Select value={formData.prioridade} onValueChange={(v) => setFormData({...formData, prioridade: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baixa">{language === 'pt-BR' ? 'Baixa' : 'Low'}</SelectItem>
                  <SelectItem value="media">{language === 'pt-BR' ? 'Média' : 'Medium'}</SelectItem>
                  <SelectItem value="alta">{language === 'pt-BR' ? 'Alta' : 'High'}</SelectItem>
                  <SelectItem value="urgente">🚨 {language === 'pt-BR' ? 'Urgente' : 'Urgent'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>{language === 'pt-BR' ? 'Título (resumo)' : 'Title (summary)'} *</Label>
            <Input 
              value={formData.titulo}
              onChange={(e) => setFormData({...formData, titulo: e.target.value})}
              placeholder={language === 'pt-BR' ? 'Ex: Troca de pneu realizada' : 'E.g., Tire changed'}
            />
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Descrição Detalhada' : 'Detailed Description'} *</Label>
            <Textarea 
              value={formData.descricao}
              onChange={(e) => setFormData({...formData, descricao: e.target.value})}
              placeholder={language === 'pt-BR' ? 'Descreva o que aconteceu...' : 'Describe what happened...'}
              rows={4}
            />
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Localização (opcional)' : 'Location (optional)'}</Label>
            <Input 
              value={formData.localizacao}
              onChange={(e) => setFormData({...formData, localizacao: e.target.value})}
              placeholder={language === 'pt-BR' ? 'Ex: Highway 101, Exit 45' : 'E.g., Highway 101, Exit 45'}
            />
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Custo Adicional (opcional)' : 'Additional Cost (optional)'}</Label>
            <div className="flex items-center gap-2">
              <span className="text-lg">$</span>
              <Input 
                type="number"
                step="0.01"
                value={formData.custo_adicional}
                onChange={(e) => setFormData({...formData, custo_adicional: parseFloat(e.target.value) || 0})}
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <Label>{language === 'pt-BR' ? 'Fotos (opcional)' : 'Photos (optional)'}</Label>
            <div className="border-2 border-dashed rounded-lg p-4 text-center">
              <input 
                type="file" 
                multiple 
                accept="image/*" 
                onChange={handleFileUpload}
                className="hidden" 
                id="foto-upload"
                disabled={uploadingPhotos}
              />
              <label htmlFor="foto-upload" className="cursor-pointer">
                {uploadingPhotos ? (
                  <Loader2 className="h-8 w-8 mx-auto text-blue-500 animate-spin mb-2" />
                ) : (
                  <Camera className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                )}
                <p className="text-sm text-gray-600">
                  {uploadingPhotos 
                    ? (language === 'pt-BR' ? 'Enviando...' : 'Uploading...')
                    : (language === 'pt-BR' ? 'Clique para adicionar fotos' : 'Click to add photos')
                  }
                </p>
              </label>
            </div>
            
            {fotos.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {fotos.map((foto, idx) => (
                  <div key={idx} className="relative group">
                    <img src={foto} alt={`Foto ${idx+1}`} className="w-20 h-20 object-cover rounded border" />
                    <button 
                      type="button"
                      onClick={() => setFotos(fotos.filter((_, i) => i !== idx))} 
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading || uploadingPhotos}>
            {language === 'pt-BR' ? 'Cancelar' : 'Cancel'}
          </Button>
          <Button onClick={handleSubmit} disabled={loading || uploadingPhotos} className="bg-green-600 hover:bg-green-700">
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {language === 'pt-BR' ? 'Salvando...' : 'Saving...'}
              </>
            ) : (
              language === 'pt-BR' ? '✅ Salvar' : '✅ Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RentalUpdateModal;
